import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Spinner, StackView, Tooltip } from '@telus-uds/components-web';
import Edit from '@telus-uds/palette-allium/build/web/icons/Edit';
import Delete from '@telus-uds/palette-allium/build/web/icons/Delete';
import { useLocaleContext } from '../../../context/LocaleContext';
import { deviceQuestions } from './constants';
import {
  ResponsiveTableContainer,
  PaginationContainer,
  PaginationBar,
  PageButton,
  NavigationButton
} from './css';

const DeviceTable = ({ 
  devices, 
  loading, 
  totalDevices, 
  currentPage, 
  totalPages, 
  onEdit, 
  onDelete, 
  onPageChange, 
  onPreviousPage, 
  onNextPage, 
  DEVICE_MATRIX 
}) => {
  const locale = useLocaleContext();

  // Helper function to scroll to top of table
  const scrollToTop = () => {
    const tableContainer = document.querySelector('.responsive-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Wrapper functions for pagination with scroll to top
  const handlePageChange = (page) => {
    onPageChange(page);
    scrollToTop();
  };

  const handlePreviousPage = () => {
    onPreviousPage();
    scrollToTop();
  };

  const handleNextPage = () => {
    onNextPage();
    scrollToTop();
  };

  // Version-specific renderer: maps stored value (No/Yes) to display label (Version 1/Version 2)
  const renderVersionValue = (value) => {
    const versionQuestion = deviceQuestions.find(q => q.key === 'customerRequireAIFeatures');
    if (!versionQuestion || !value) return '-';

    const resolveLabel = (storedEn) => {
      const option = versionQuestion.options.find(
        o => o.en.toLowerCase() === storedEn.toLowerCase() || o.fr.toLowerCase() === storedEn.toLowerCase()
      );
      if (option && option.displayLabel) {
        return locale === 'en-US' ? option.displayLabel.en : option.displayLabel.fr;
      }
      return storedEn;
    };

    let labels;
    if (Array.isArray(value)) {
      labels = value.map(item => {
        const enVal = typeof item === 'object' ? item.en : item;
        return resolveLabel(enVal);
      });
    } else if (typeof value === 'string') {
      labels = value.split(',').map(v => resolveLabel(v.trim()));
    } else {
      labels = [resolveLabel(value)];
    }

    const uniqueLabels = [...new Set(labels)];

    return (
      <div style={{ color: '#333', wordWrap: 'break-word', maxWidth: '120px' }}>
        {uniqueLabels.join(', ')}
      </div>
    );
  };

  // Helper function to render individual configuration values
  const renderConfigValue = (key, value) => {
    if (!value) return '-';
    
    let displayValue = value;
    
    if (Array.isArray(value)) {
      displayValue = value.map(item => {
        if (typeof item === 'object' && item.en) {
          return locale === 'en-US' ? item.en : item.fr;
        }
        return item;
      }).join(', ');
    } else if (typeof value === 'object' && value.en) {
      displayValue = locale === 'en-US' ? value.en : value.fr;
    } else if (typeof value === 'string') {
      // Handle comma-separated strings like "Yes, Oui, No, Non"
      const values = value.split(',').map(v => v.trim());
      const displayValues = [];
      const seenValues = new Set();
      
      if (locale === 'en-US') {
        // Extract English values
        values.forEach(val => {
          if (!seenValues.has(val)) {
            const isFrenchTerm = /^(Oui|Non|Nouveau|Ajouter|Voix PLUS|Amélioré|Complet|Complet PLUS|Mobile|Voix|sur une base mensuelle|3 ans|5 ans|Groupé|D'appareils additionnels|Crédit matériel|Version de Connexion Affaires|Type d'installation|Durée|Segment|C2F)$/i.test(val);
            if (!isFrenchTerm) {
              displayValues.push(val);
              seenValues.add(val);
            }
          }
        });
      } else {
        // Extract French values
        values.forEach(val => {
          if (!seenValues.has(val)) {
            const isEnglishTerm = /^(Yes|No|New|Add On|Voice \+|Enhanced|Complete|Complete \+|Mobile|Voice|Month-to-Month|3 years|5 years|Bundled|Spare Devices|Hardware Credit|Business Connect Edition|Type of Installation|Term|Segment|C2F)$/i.test(val);
            if (!isEnglishTerm || /^(SMB|Commercial|\d+)$/i.test(val)) {
              displayValues.push(val);
              seenValues.add(val);
            }
          }
        });
      }
      
      displayValue = displayValues.length > 0 ? displayValues.join(', ') : value;
    }
    
    return (
      <div style={{ 
        color: '#333',
        wordWrap: 'break-word',
        maxWidth: '120px'
      }}>
        {displayValue}
      </div>
    );
  };

  const headerList = {
    NAME: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.DEVICE_NAME) || 'Device Name',
    DEVICE_TYPE: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.DEVICE_TYPE) || 'Device Type',
    FAMILY_TYPE: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.FAMILY_TYPE) || 'Family Type',
    SKU: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.SKU) || 'SKU',
    ONE_TIME_PRICE: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.ONE_TIME_PRICE) || 'One Time Price',
    EASY_PAYMENT_PRICE: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.EASY_PAYMENT_PRICE) || 'Easy Payment Price',
    C2F: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.C2F) || 'C2F',
    SEGMENT: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.SEGMENT) || 'Segment',
    TERM: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.TERM) || 'Term',
    VERSION: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.VERSION) || 'Version',
    TYPE_OF_INSTALLATION: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.TYPE_OF_INSTALLATION) || 'Type of Installation',
    BUSINESS_CONNECT_EDITION: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.BUSINESS_CONNECT_EDITION) || 'Business Connect Edition',
    BUNDLED: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.BUNDLED) || 'Bundled',
    SPARE_DEVICES: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.SPARE_DEVICES) || 'Spare Devices',
    HARDWARE_CREDIT: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.HARDWARE_CREDIT) || 'Hardware Credit',
    CREATED: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.CREATED_AT) || 'Created At',
    UPDATED: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.UPDATED_AT) || 'Updated At',
    ACTIONS: (DEVICE_MATRIX.COLUMNS && DEVICE_MATRIX.COLUMNS.ACTIONS) || 'Actions'
  };

  return (
    <StackView space={3}>
      <Spinner fullScreen label={(DEVICE_MATRIX.LOADING && DEVICE_MATRIX.LOADING.DEVICES) || 'Loading devices...'} show={loading} />
      
      <ResponsiveTableContainer>
        <div className="responsive-table-container">
          <table className="responsive-table">
            <caption className="responsive-table__caption">
              {DEVICE_MATRIX.TITLE || 'Device Matrix'} ({totalDevices})
            </caption>
            <thead className="responsive-table__thead">
              <tr>
                {Object.values(headerList).map(header => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="responsive-table__tbody">
              {devices.map(device => (
                <tr key={device.id}>
                  <td data-header-label={headerList.NAME}>{device.name}</td>
                  <td data-header-label={headerList.DEVICE_TYPE}>{device.deviceType}</td>
                  <td data-header-label={headerList.FAMILY_TYPE}>{device.familyType}</td>
                  <td data-header-label={headerList.SKU}>{device.deviceSku}</td>
                  <td data-header-label={headerList.ONE_TIME_PRICE}>{device.oneTimePrice}</td>
                  <td data-header-label={headerList.EASY_PAYMENT_PRICE}>{device.easyPaymentPrice}</td>
                  <td data-header-label={headerList.C2F}>
                    {renderConfigValue('coperToFibreMigration', device.answers && device.answers.coperToFibreMigration)}
                  </td>
                  <td data-header-label={headerList.SEGMENT}>
                    {renderConfigValue('customerSegment', device.answers && device.answers.customerSegment)}
                  </td>
                  <td data-header-label={headerList.TERM}>
                    {renderConfigValue('contractTerm', device.answers && device.answers.contractTerm)}
                  </td>
                  <td data-header-label={headerList.VERSION}>
                    {renderVersionValue(device.answers && device.answers.customerRequireAIFeatures)}
                  </td>
                  <td data-header-label={headerList.TYPE_OF_INSTALLATION}>
                    {renderConfigValue('typeOfInstallation', device.answers && device.answers.typeOfInstallation)}
                  </td>
                  <td data-header-label={headerList.BUSINESS_CONNECT_EDITION}>
                    {renderConfigValue('businessConnectEdition', device.answers && device.answers.businessConnectEdition)}
                  </td>
                  <td data-header-label={headerList.BUNDLED}>
                    {renderConfigValue('otherTelusServices', device.answers && device.answers.otherTelusServices)}
                  </td>
                  <td data-header-label={headerList.SPARE_DEVICES}>
                    {renderConfigValue('doesCustomerRequireSpareDevices', device.answers && device.answers.doesCustomerRequireSpareDevices)}
                  </td>
                  <td data-header-label={headerList.HARDWARE_CREDIT}>
                    {renderConfigValue('hardwareCredir', device.answers && device.answers.hardwareCredir)}
                  </td>
                  <td data-header-label={headerList.CREATED}>
                    {new Date(device.created_at).toLocaleDateString()}
                  </td>
                  <td data-header-label={headerList.UPDATED}>
                    {new Date(device.updated_at).toLocaleDateString()}
                  </td>
                  <td data-header-label={headerList.ACTIONS}>
                    <div className="responsive-table__actions">
                      <Tooltip content={(DEVICE_MATRIX.BUTTONS && DEVICE_MATRIX.BUTTONS.EDIT) || 'Edit'}>
                        <Button
                          icon={Edit}
                          accessibilityLabel={(DEVICE_MATRIX.BUTTONS && DEVICE_MATRIX.BUTTONS.EDIT) || 'Edit'}
                          onPress={() => onEdit(device)}
                          variant={{ text: true }}
                        />
                      </Tooltip>
                      <Tooltip content={(DEVICE_MATRIX.BUTTONS && DEVICE_MATRIX.BUTTONS.DELETE) || 'Delete'}>
                        <Button
                          icon={Delete}
                          accessibilityLabel={(DEVICE_MATRIX.BUTTONS && DEVICE_MATRIX.BUTTONS.DELETE) || 'Delete'}
                          onPress={() => onDelete(device)}
                          variant={{ text: true }}
                        />
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ResponsiveTableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationBar>
            <NavigationButton 
              onClick={handlePreviousPage} 
              disabled={currentPage === 1}
            >
              ←
            </NavigationButton>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PageButton
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PageButton>
            ))}
            
            <NavigationButton 
              onClick={handleNextPage} 
              disabled={currentPage === totalPages}
            >
              →
            </NavigationButton>
          </PaginationBar>
        </PaginationContainer>
      )}
    </StackView>
  );
};

DeviceTable.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    deviceType: PropTypes.string,
    familyType: PropTypes.string,
    deviceSku: PropTypes.string,
    oneTimePrice: PropTypes.string,
    easyPaymentPrice: PropTypes.string,
    answers: PropTypes.shape({
      coperToFibreMigration: PropTypes.string,
      customerSegment: PropTypes.string,
      contractTerm: PropTypes.string,
      typeOfInstallation: PropTypes.string,
      businessConnectEdition: PropTypes.string,
      otherTelusServices: PropTypes.string,
      doesCustomerRequireSpareDevices: PropTypes.string,
      hardwareCredir: PropTypes.string
    }),
    created_at: PropTypes.string,
    updated_at: PropTypes.string
  })).isRequired,
  loading: PropTypes.bool.isRequired,
  totalDevices: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPreviousPage: PropTypes.func.isRequired,
  onNextPage: PropTypes.func.isRequired,
  DEVICE_MATRIX: PropTypes.shape({
    TITLE: PropTypes.string,
    COLUMNS: PropTypes.shape({
      DEVICE_NAME: PropTypes.string,
      DEVICE_TYPE: PropTypes.string,
      FAMILY_TYPE: PropTypes.string,
      SKU: PropTypes.string,
      ONE_TIME_PRICE: PropTypes.string,
      EASY_PAYMENT_PRICE: PropTypes.string,
      C2F: PropTypes.string,
      SEGMENT: PropTypes.string,
      TERM: PropTypes.string,
      VERSION: PropTypes.string,
      TYPE_OF_INSTALLATION: PropTypes.string,
      BUSINESS_CONNECT_EDITION: PropTypes.string,
      BUNDLED: PropTypes.string,
      SPARE_DEVICES: PropTypes.string,
      HARDWARE_CREDIT: PropTypes.string,
      CONFIGURATION: PropTypes.string,
      CREATED_AT: PropTypes.string,
      UPDATED_AT: PropTypes.string,
      ACTIONS: PropTypes.string
    }),
    BUTTONS: PropTypes.shape({
      EDIT: PropTypes.string,
      DELETE: PropTypes.string
    }),
    LOADING: PropTypes.shape({
      DEVICES: PropTypes.string
    }),
    MESSAGES: PropTypes.shape({
      NO_CONFIGURATION: PropTypes.string
    })
  }).isRequired
};

export default DeviceTable;
